import React, { useEffect, useState } from 'react';
import { Sidebar } from './Sidebar';
import { LayoutProps } from '@/types/components';
import { APP_CONFIG } from '@/config/app';

export const Layout: React.FC<LayoutProps> = ({
  children,
  currentPath = '/',
}) => {
  // Desktop: collapse the sidebar to a rail. Mobile: open it as an
  // off-canvas drawer. These are independent — a phone never uses the rail.
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setIsMobileOpen(false);
  }, [currentPath]);

  return (
    <div className="flex h-screen bg-bg-weak-50 overflow-hidden">
      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-bg-strong-950/50 md:hidden"
          aria-hidden="true"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar — off-canvas drawer below md, in-flow column at md+. On
          mobile it's always the full menu (never the rail). */}
      <div
        className={`fixed inset-y-0 left-0 z-50 transition-transform duration-300 md:static md:z-auto md:translate-x-0 ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <Sidebar
          isCollapsed={isCollapsed}
          onToggleCollapse={() => setIsCollapsed((v) => !v)}
          onNavigate={() => setIsMobileOpen(false)}
          currentPath={currentPath}
        />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-bg-white-0 border-b border-stroke-soft-200 px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setIsMobileOpen(true)}
                className="md:hidden p-2 rounded-lg hover:bg-bg-weak-50 transition-colors"
                aria-label="Open menu"
              >
                <svg
                  className="w-6 h-6 text-icon-sub-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>

              {/* Brand logo — mobile only (the desktop sidebar shows it). */}
              <img
                src="/images/logo.png"
                alt={`${APP_CONFIG.name} logo`}
                className="h-7 w-auto md:hidden"
              />
            </div>

            <div className="flex items-center space-x-4"></div>
          </div>
        </header>

        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
};
